import React, { useState, useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatsApp from '@mui/icons-material/WhatsApp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
  getOrderTotals,
  openWhatsAppOrder,
} from '../../utils/whatsappOrder';

const labels = {
  en: {
    cart: 'Your Order',
    stepItems: 'Items',
    stepDetails: 'Details',
    stepReview: 'Review',
    empty: 'Your cart is empty',
    addItems: 'Add items from the menu to get started',
    itemNote: 'Item note (optional)',
    generalNote: 'Order note (optional)',
    customerName: 'Your name',
    customerNameRequired: 'Please enter your name',
    orderType: 'Order type',
    delivery: 'Delivery',
    pickup: 'Pickup',
    orderTypeRequired: 'Please select delivery or pickup',
    deliveryAddress: 'Delivery address',
    deliveryAddressRequired: 'Please enter your delivery address',
    addressLabel: 'Address',
    subtotal: 'Subtotal',
    tax: 'Tax',
    service: 'Service',
    grandTotal: 'Total',
    total: 'Total',
    send: 'Send Order via WhatsApp',
    next: 'Continue',
    back: 'Back',
    review: 'Review order',
    qty: 'Qty',
    remove: 'Remove',
    orderError: 'Unable to open WhatsApp. Please try again.',
    nameLabel: 'Customer',
    typeLabel: 'Type',
    noteLabel: 'Note',
  },
  ar: {
    cart: 'طلبك',
    stepItems: 'الأصناف',
    stepDetails: 'التفاصيل',
    stepReview: 'المراجعة',
    empty: 'السلة فارغة',
    addItems: 'أضف منتجات من القائمة للبدء',
    itemNote: 'ملاحظة على الصنف (اختياري)',
    generalNote: 'ملاحظة على الطلب (اختياري)',
    customerName: 'اسمك',
    customerNameRequired: 'يرجى إدخال اسمك',
    orderType: 'نوع الطلب',
    delivery: 'توصيل',
    pickup: 'استلام',
    orderTypeRequired: 'يرجى اختيار توصيل أو استلام',
    deliveryAddress: 'عنوان التوصيل',
    deliveryAddressRequired: 'يرجى إدخال عنوان التوصيل',
    addressLabel: 'العنوان',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    service: 'رسوم الخدمة',
    grandTotal: 'الإجمالي',
    total: 'المجموع',
    send: 'إرسال الطلب عبر واتساب',
    next: 'متابعة',
    back: 'رجوع',
    review: 'مراجعة الطلب',
    qty: 'الكمية',
    remove: 'حذف',
    orderError: 'تعذر فتح واتساب. حاول مرة أخرى.',
    nameLabel: 'العميل',
    typeLabel: 'النوع',
    noteLabel: 'ملاحظة',
  },
};

const STEPS = ['items', 'details', 'review'];

const textFieldSx = (primaryColor) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    borderRadius: 1.5,
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: primaryColor },
    '&.Mui-focused fieldset': { borderColor: primaryColor },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
});

function CartTotals({ cart, restaurant, t, currency, primaryColor }) {
  const { subtotal, serviceRate, serviceAmount, taxRate, taxAmount, grandTotal } = getOrderTotals(cart, restaurant);
  const hasExtras = serviceRate > 0 || taxRate > 0;

  if (!hasExtras) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 700 }}>{t.total}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: primaryColor }}>
          {currency}{subtotal.toFixed(2)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>{t.subtotal}</Typography>
        <Typography sx={{ fontWeight: 600 }}>{currency}{subtotal.toFixed(2)}</Typography>
      </Box>
      {serviceRate > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
            {t.service} ({serviceRate}%)
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>{currency}{serviceAmount.toFixed(2)}</Typography>
        </Box>
      )}
      {taxRate > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
            {t.tax} ({taxRate}%)
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>{currency}{taxAmount.toFixed(2)}</Typography>
        </Box>
      )}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{t.grandTotal}</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: primaryColor }}>
          {currency}{grandTotal.toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
}

function StepIndicator({ step, t, primaryColor }) {
  const stepLabels = [t.stepItems, t.stepDetails, t.stepReview];

  return (
    <Box className="menu-cart-steps">
      {STEPS.map((_, index) => (
        <Box key={index} className="menu-cart-step">
          <Box
            className={`menu-cart-step__dot${index <= step ? ' menu-cart-step__dot--active' : ''}`}
            style={index <= step ? { background: primaryColor, borderColor: primaryColor } : {}}
          >
            {index + 1}
          </Box>
          <Typography
            className={`menu-cart-step__label${index === step ? ' menu-cart-step__label--active' : ''}`}
            style={index === step ? { color: primaryColor } : {}}
          >
            {stepLabels[index]}
          </Typography>
          {index < STEPS.length - 1 && (
            <Box
              className={`menu-cart-step__line${index < step ? ' menu-cart-step__line--active' : ''}`}
              style={index < step ? { background: primaryColor } : {}}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function MenuCart({
  open,
  onClose,
  cart,
  setCart,
  restaurant,
  language,
  primaryColor,
}) {
  const t = labels[language === 'ar' ? 'ar' : 'en'];
  const currency = restaurant?.currency ? `${restaurant.currency} ` : '';

  const [step, setStep] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [orderType, setOrderType] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [generalNote, setGeneralNote] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!open) {
      setStep(0);
      setFormError('');
      setDeliveryAddress('');
    }
  }, [open]);

  const updateQuantity = (cartId, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.cartId === cartId
            ? { ...item, quantity: Math.max(1, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (cartId) => {
    setCart((prev) => prev.filter((item) => item.cartId !== cartId));
  };

  const updateItemNote = (cartId, note) => {
    setCart((prev) =>
      prev.map((item) => (item.cartId === cartId ? { ...item, note } : item))
    );
  };

  const getItemName = (item) => {
    if (language === 'ar' && item.nameAr) return item.nameAr;
    return item.name;
  };

  const getOrderTypeLabel = () => {
    if (orderType === 'delivery') return t.delivery;
    if (orderType === 'pickup') return t.pickup;
    return '';
  };

  const handleNext = () => {
    setFormError('');
    if (step === 0 && cart.length === 0) return;

    if (step === 1) {
      if (!customerName.trim()) {
        setFormError(t.customerNameRequired);
        return;
      }
      if (!orderType) {
        setFormError(t.orderTypeRequired);
        return;
      }
      if (orderType === 'delivery' && !deliveryAddress.trim()) {
        setFormError(t.deliveryAddressRequired);
        return;
      }
    }

    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setFormError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSendOrder = () => {
    const result = openWhatsAppOrder({
      restaurant,
      cart,
      generalNote,
      customerName: customerName.trim(),
      orderType,
      deliveryAddress: deliveryAddress.trim(),
      language,
    });
    if (!result.success) {
      alert(result.message || t.orderError);
    }
  };

  const renderItemsStep = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '50vh', overflowY: 'auto', mb: 2 }}>
        {cart.map((item) => {
          const lineTotal = (parseFloat(item.price) || 0) * item.quantity;
          return (
            <Box
              key={item.cartId}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(39, 30, 28, 0.8)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{getItemName(item)}</Typography>
                  <Typography sx={{ color: primaryColor, fontWeight: 700, mt: 0.5 }}>
                    {currency}{lineTotal.toFixed(2)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removeItem(item.cartId)}
                  sx={{ color: 'rgba(255,255,255,0.5)' }}
                  aria-label={t.remove}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{t.qty}</Typography>
                <IconButton
                  size="small"
                  onClick={() => updateQuantity(item.cartId, -1)}
                  sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#fff', width: 32, height: 32 }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>{item.quantity}</Typography>
                <IconButton
                  size="small"
                  onClick={() => updateQuantity(item.cartId, 1)}
                  sx={{
                    bgcolor: primaryColor,
                    color: '#fff',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: primaryColor, opacity: 0.9 },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder={t.itemNote}
                value={item.note || ''}
                onChange={(e) => updateItemNote(item.cartId, e.target.value)}
                sx={{ mt: 1.5, ...textFieldSx(primaryColor) }}
              />
            </Box>
          );
        })}
      </Box>

      <CartTotals cart={cart} restaurant={restaurant} t={t} currency={currency} primaryColor={primaryColor} />
    </>
  );

  const renderDetailsStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2 }}>
      <TextField
        fullWidth
        required
        label={t.customerName}
        value={customerName}
        onChange={(e) => {
          setCustomerName(e.target.value);
          setFormError('');
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonOutlineIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={textFieldSx(primaryColor)}
      />

      <Box>
        <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.9rem' }}>{t.orderType}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <button
            type="button"
            className={`menu-order-type-btn${orderType === 'delivery' ? ' menu-order-type-btn--active' : ''}`}
            onClick={() => {
              setOrderType('delivery');
              setFormError('');
            }}
            style={orderType === 'delivery' ? { borderColor: primaryColor, background: `color-mix(in srgb, ${primaryColor} 20%, transparent)` } : {}}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 28 }} />
            <span>{t.delivery}</span>
          </button>
          <button
            type="button"
            className={`menu-order-type-btn${orderType === 'pickup' ? ' menu-order-type-btn--active' : ''}`}
            onClick={() => {
              setOrderType('pickup');
              setDeliveryAddress('');
              setFormError('');
            }}
            style={orderType === 'pickup' ? { borderColor: primaryColor, background: `color-mix(in srgb, ${primaryColor} 20%, transparent)` } : {}}
          >
            <StorefrontOutlinedIcon sx={{ fontSize: 28 }} />
            <span>{t.pickup}</span>
          </button>
        </Box>
      </Box>

      {orderType === 'delivery' && (
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label={t.deliveryAddress}
          value={deliveryAddress}
          onChange={(e) => {
            setDeliveryAddress(e.target.value);
            setFormError('');
          }}
          placeholder={language === 'ar' ? 'الشارع، المبنى، الطابق، المنطقة...' : 'Street, building, floor, area...'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.2 }}>
                <LocationOnOutlinedIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={textFieldSx(primaryColor)}
        />
      )}

      <TextField
        fullWidth
        multiline
        minRows={2}
        label={t.generalNote}
        value={generalNote}
        onChange={(e) => setGeneralNote(e.target.value)}
        sx={textFieldSx(primaryColor)}
      />
    </Box>
  );

  const renderReviewStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, maxHeight: '55vh', overflowY: 'auto' }}>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(39, 30, 28, 0.8)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>{t.nameLabel}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{customerName}</Typography>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>{t.typeLabel}</Typography>
        <Typography sx={{ fontWeight: 700 }}>{getOrderTypeLabel()}</Typography>
        {orderType === 'delivery' && deliveryAddress.trim() && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>{t.addressLabel}</Typography>
            <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>{deliveryAddress}</Typography>
          </>
        )}
        {generalNote.trim() && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1.5 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>{t.noteLabel}</Typography>
            <Typography sx={{ fontSize: '0.9rem' }}>{generalNote}</Typography>
          </>
        )}
      </Box>

      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.review}</Typography>

      {cart.map((item) => {
        const lineTotal = (parseFloat(item.price) || 0) * item.quantity;
        return (
          <Box
            key={item.cartId}
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(39, 30, 28, 0.6)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {getItemName(item)} × {item.quantity}
              </Typography>
              <Typography sx={{ color: primaryColor, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {currency}{lineTotal.toFixed(2)}
              </Typography>
            </Box>
            {item.note?.trim() && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mt: 0.5 }}>
                📝 {item.note}
              </Typography>
            )}
          </Box>
        );
      })}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      <CartTotals cart={cart} restaurant={restaurant} t={t} currency={currency} primaryColor={primaryColor} />
    </Box>
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '92vh',
          bgcolor: '#1a1410',
          color: '#fff',
        },
      }}
    >
      <Box sx={{ p: 2, pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon sx={{ color: primaryColor }} />
            {t.cart}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close cart">
            <CloseIcon />
          </IconButton>
        </Box>

        {cart.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ShoppingCartIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)', mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>{t.empty}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
              {t.addItems}
            </Typography>
          </Box>
        ) : (
          <>
            <StepIndicator step={step} t={t} primaryColor={primaryColor} />

            {step === 0 && renderItemsStep()}
            {step === 1 && renderDetailsStep()}
            {step === 2 && renderReviewStep()}

            {formError && (
              <Typography sx={{ color: '#f87171', fontSize: '0.875rem', mb: 1.5, textAlign: 'center' }}>
                {formError}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 ,flexDirection: 'column' }}>
              {step > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    flex: 1,
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.25)',
                    py: 1.25,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: primaryColor, bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  {t.back}
                </Button>
              )}

              {step < 2 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    flex: 1,
                    bgcolor: primaryColor,
                    py: 1.25,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: `0 4px 14px color-mix(in srgb, ${primaryColor} 40%, transparent)`,
                    '&:hover': { bgcolor: primaryColor, opacity: 0.92 },
                  }}
                >
                  {t.next}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSendOrder}
                  startIcon={<WhatsApp />}
                  sx={{
                    flex: 1,
                    bgcolor: '#25D366',
                    py: 1.25,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    boxShadow: '0 4px 14px rgba(37, 211, 102, 0.4)',
                    '&:hover': { bgcolor: '#20bd5a' },
                  }}
                >
                  {t.send}
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}

export function MenuCartFab({ itemCount, onClick, primaryColor, language }) {
  if (itemCount === 0) return null;

  return (
    <button
      type="button"
      className="menu-cart-fab"
      onClick={onClick}
      style={{ '--menu-primary': primaryColor }}
      aria-label={language === 'ar' ? 'عرض السلة' : 'View cart'}
    >
      <ShoppingCartIcon />
      <span className="menu-cart-fab__badge">{itemCount}</span>
    </button>
  );
}
